/**
 * AdlIadlTable — renders the 19-row ADL/IADL functional assessment table
 * (Phase X.3.5). The table is a signature element of Peace assessment
 * reports; the schema guarantees all 19 activities are present.
 *
 * Drafter founder polish (2026-07-06, item 3): the table is EDITABLE when
 * the caller supplies onEntryChange. Activity names stay fixed (the closed
 * 19-activity catalog); Level edits through a select carrying the closed
 * level vocabulary (the schema's ADL_IADL_LEVELS set), and Notes edits
 * through an inline text input. Direct edits win: each change bubbles the
 * patched entry up, rides the page's response state into Save, the refine
 * payload, and the .docx, and marks the row with the edited-by-you trust
 * state (the same hairline the prose leaves use). Read-only when
 * onEntryChange is absent (drafts detail reuse).
 *
 * Warm Peace palette (forest header, sage-tint divider).
 */

import { type CSSProperties, type ReactNode } from 'react';
import type { AdlIadlEntry, AdlIadlLevel } from '@/lib/api';
import type { ParagraphState } from './AssessmentDraftOutput';
import { SelectField } from '@/components/primitives/SelectField';

/**
 * The closed level vocabulary, mirroring ADL_IADL_LEVELS in
 * api/_shared/aiTools/schemas/assessmentDraftResponse.ts (the validator
 * rejects anything else, so the select must never offer another value).
 */
export const ADL_IADL_LEVEL_OPTIONS: readonly AdlIadlLevel[] = [
  'Independent',
  'Partial Assist',
  'Dependent',
  'Not assessed',
];

export interface AdlIadlTableProps {
  entries: readonly AdlIadlEntry[];
  /**
   * Present = the table is editable (Level + Notes per row; the activity
   * catalog is closed). Called with the row's activity and the patch.
   */
  onEntryChange?: (activity: string, patch: Partial<Pick<AdlIadlEntry, 'level' | 'notes'>>) => void;
  /** Per-row trust states, keyed by activity ('edited' | 'changed'). */
  entryStates?: Record<string, ParagraphState | undefined>;
  /** A row a change-run touched was viewed (hover/focus); clears 'changed'. */
  onEntrySeen?: (activity: string) => void;
}

const HEAD_STYLE = { color: 'var(--color-sage-800)' } as const;
const BODY_STYLE = { color: 'var(--color-ink)' } as const;

// Quiet inline controls: read as table text until hovered/focused.
const CONTROL_CLS =
  'w-full rounded-[6px] border border-transparent bg-transparent px-1 py-0.5 text-sm text-ink ' +
  'transition-colors duration-[var(--dur-base)] hover:border-sage-300 hover:bg-white ' +
  'focus-visible:outline-none focus-visible:border-sage-400 focus-visible:ring-1 focus-visible:ring-sage-400';

/** Trust-state hairline on the row's first cell (mirrors EditableProse). */
function rowHairline(state: ParagraphState | undefined): CSSProperties {
  return state === 'edited'
    ? { borderLeft: '2px solid var(--color-sage-400)', paddingLeft: '8px' }
    : state === 'changed'
      ? { borderLeft: '2px solid var(--color-warm-400)', paddingLeft: '8px' }
      : {};
}

export function AdlIadlTable({
  entries,
  onEntryChange,
  entryStates,
  onEntrySeen,
}: AdlIadlTableProps): ReactNode {
  const editable = onEntryChange !== undefined;

  return (
    <div className="overflow-x-auto" data-testid="adl-iadl-table">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b" style={{ borderColor: 'var(--color-sage-300)' }}>
            <th className="text-left font-semibold py-2 pr-3" style={HEAD_STYLE}>
              Activity
            </th>
            <th className="text-left font-semibold py-2 pr-3" style={HEAD_STYLE}>
              Level
            </th>
            <th className="text-left font-semibold py-2" style={HEAD_STYLE}>
              Notes
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const state = entryStates?.[e.activity];
            const seenHandlers =
              state === 'changed' && onEntrySeen
                ? { onMouseEnter: () => onEntrySeen(e.activity), onFocus: () => onEntrySeen(e.activity) }
                : {};
            return (
              <tr
                key={e.activity}
                className="border-b"
                style={{ borderColor: 'var(--color-sage-300)' }}
                data-testid={`adl-iadl-row-${e.activity}`}
                data-paragraph-state={state ?? 'drafted'}
                {...seenHandlers}
              >
                <td className="py-1.5 pr-3 align-top" style={{ ...BODY_STYLE, ...rowHairline(state) }}>
                  {e.activity}
                </td>
                <td className="py-1.5 pr-3 align-top whitespace-nowrap" style={BODY_STYLE}>
                  {editable ? (
                    <SelectField size="compact" quiet
                      className="w-full min-w-[9rem]"
                      value={e.level}
                      onChange={(ev) =>
                        onEntryChange(e.activity, { level: ev.target.value as AdlIadlLevel })
                      }
                      aria-label={`Level for ${e.activity}`}
                      data-testid={`adl-iadl-level-${e.activity}`}
                    >
                      {ADL_IADL_LEVEL_OPTIONS.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </SelectField>
                  ) : (
                    e.level
                  )}
                </td>
                <td className="py-1.5 align-top" style={BODY_STYLE}>
                  {editable ? (
                    <input
                      type="text"
                      className={CONTROL_CLS}
                      value={e.notes}
                      onChange={(ev) => onEntryChange(e.activity, { notes: ev.target.value })}
                      placeholder="Add a note"
                      aria-label={`Notes for ${e.activity}`}
                      data-testid={`adl-iadl-notes-${e.activity}`}
                    />
                  ) : (
                    e.notes
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
